import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, ParamMap } from '@angular/router'; // Importiere Router-Module
import { AssessmentDefinition, SideType } from '../../models/assessment-definition.model';
import { AssessmentResult, GroupResult, TaskResult } from '../../models/assessment-result.model';
import { AssessmentDefinitionService } from '../../services/assessment-definition.service';
import { AssessmentResultService } from '../../services/assessment-result.service';
import { CalculationService } from '../../services/calculation.service';
import { DynamicInputComponent } from '../dynamic-input/dynamic-input.component';

// Angular Material Imports
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-assessment-container',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DynamicInputComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatExpansionModule,
  ],
  templateUrl: './assessment-container.component.html',
  styleUrls: ['./assessment-container.component.scss'],
})
export class AssessmentContainerComponent implements OnInit {
  @Input() patientId!: string;
  @Input() resultId?: string;
  @Output() completed = new EventEmitter<AssessmentResult>();
  @Output() canceled = new EventEmitter<void>();

  assessmentId!: string;
  assessment?: AssessmentDefinition;
  result?: AssessmentResult;
  form!: FormGroup;
  loading = true;
  error = '';

  // Hilfsvariablen für die UI
  sideTypeEnum = SideType;
  showGuides: { [key: string]: boolean } = {};
  showResults = false;

  // Variable zum Speichern des aktuellen Guide-Zustands
  allGuidesVisible: boolean = false;

  constructor(
    private fb: FormBuilder,
    private assessmentService: AssessmentDefinitionService,
    private resultService: AssessmentResultService,
    private calculationService: CalculationService,
    private route: ActivatedRoute, // Injiziere ActivatedRoute
    private router: Router // Injiziere Router für Navigation
  ) {}

  ngOnInit(): void {
    // Hole patientId aus der Route falls nicht als Input bereitgestellt
    if (!this.patientId) {
      this.route.parent?.paramMap.subscribe(params => {
        this.patientId = params.get('patientId') || '';
        if (!this.patientId) {
          this.error = 'Keine Patienten-ID gefunden.';
          return;
        }
      });
    }

    // Hole assessmentId aus der URL
    this.route.paramMap.subscribe(params => {
      const paramId = params.get('assessmentId');
      if (paramId) {
        this.assessmentId = paramId;
        this.loadAssessment();
      } else {
        // Prüfe, ob assessmentId als Query-Parameter vorliegt
        this.route.queryParamMap.subscribe(queryParams => {
          const queryId = queryParams.get('id');
          if (queryId) {
            this.assessmentId = queryId;
            this.loadAssessment();
          } else {
            this.error = 'Keine Assessment-ID gefunden.';
          }
        });
      }
    });
  }

  /**
   * Lädt die Assessment-Definition und ggf. vorhandene Ergebnisse
   */
  private loadAssessment(): void {
    if (!this.assessmentId) {
      this.error = 'Keine Assessment-ID angegeben.';
      this.loading = false;
      return;
    }

    this.loading = true;
    this.assessmentService.getAssessmentById(this.assessmentId).subscribe(
      assessment => {
        if (!assessment) {
          this.error = 'Assessment nicht gefunden.';
          this.loading = false;
          return;
        }

        this.assessment = assessment;

        // Wenn eine Result-ID vorhanden ist, das Ergebnis laden
        if (this.resultId) {
          this.resultService.getResultById(this.resultId).subscribe(result => {
            if (result) {
              this.result = result;
            } else {
              // Neues Ergebnis erstellen
              this.createNewResult();
            }
            this.initForm();
            this.loading = false;
          });
        } else {
          // Neues Ergebnis erstellen
          this.createNewResult();
          this.initForm();
          this.loading = false;
        }
      },
      error => {
        this.error = 'Fehler beim Laden des Assessments.';
        console.error(error);
        this.loading = false;
      }
    );
  }

  /**
   * Erstellt ein neues Ergebnis-Objekt
   */
  private createNewResult(): void {
    if (!this.assessment) return;

    const groupResults: GroupResult[] = this.assessment.groups.map(group => {
      const taskResults: TaskResult[] = group.tasks.map(task => {
        // Vereinfachtes TaskResult ohne value und side für beidseitige Assessments
        const result: TaskResult = {
          taskId: task.id,
        };
        
        // Für beidseitige Assessments nur die seitenspezifischen Werte initialisieren
        if (this.assessment?.sideType === SideType.Both) {
          result.leftValue = null;
          result.rightValue = null;
          // Kein "side" und "value" mehr nötig
        } else {
          result.value = null;
        }
        
        return result;
      });

      return {
        groupId: group.id,
        taskResults,
      };
    });

    this.result = {
      id: '',
      assessmentId: this.assessment.id,
      assessmentVersion: this.assessment.version,
      patientId: this.patientId,
      patientData: {
        id: this.patientId,
        birthDate: new Date('2000-01-01'), // Beispielwert, sollte durch echte Patientendaten ersetzt werden
        gender: 'male', // Beispielwert, sollte durch echte Patientendaten ersetzt werden
      },
      date: new Date(),
      groupResults,
      isComplete: false,
    };
  }

  /**
   * Initialisiert das Formular basierend auf der Assessment-Definition
   */
  private initForm(): void {
    if (!this.assessment || !this.result) return;

    const groupFormGroups = this.result.groupResults.map(groupResult => {
      const taskFormControls: any = {};

      groupResult.taskResults.forEach(taskResult => {
        const taskDef = this.getTaskDefinition(taskResult.taskId);
        if (taskDef) {
          if (this.assessment?.sideType === SideType.Both) {
            // Für beidseitige Assessments separate Kontrollen für links und rechts
            taskFormControls[`${taskResult.taskId}_left`] = [
              taskResult.leftValue, // Direkt leftValue verwenden, das bereits initialisiert wurde
              taskDef.isRequired && taskDef.isRequired === true ? Validators.required : [],
            ];
            taskFormControls[`${taskResult.taskId}_right`] = [
              taskResult.rightValue, // Direkt rightValue verwenden, das bereits initialisiert wurde
              taskDef.isRequired && taskDef.isRequired === true ? Validators.required : [],
            ];
          } else {
            // Für einseitige Assessments eine Kontrolle
            taskFormControls[taskResult.taskId] = [
              taskResult.value,
              taskDef.isRequired && taskDef.isRequired === true ? Validators.required : [],
            ];
          }

          // Zusätzliche Kontrollen für spezielle Konfigurationen (z.B. NHPT)
          if (taskDef.specialConfig) {
            Object.keys(taskDef.specialConfig).forEach(key => {
              taskFormControls[`${taskResult.taskId}_${key}`] = [
                taskResult.additionalData?.[key] || taskDef.specialConfig[key].defaultValue,
                taskDef.specialConfig[key].isRequired && taskDef.specialConfig[key].isRequired === true
                  ? Validators.required
                  : [],
              ];
            });
          }
        }
      });

      return this.fb.group(taskFormControls);
    });

    this.form = this.fb.group({
      groups: this.fb.array(groupFormGroups),
    });

    // Wenn das Ergebnis bereits vollständig ist, Ergebnisse anzeigen
    if (this.result.isComplete) {
      this.showResults = true;
    }
  }

  /**
   * Gibt die Task-Definition für eine Task-ID zurück
   */
  private getTaskDefinition(taskId: string) {
    if (!this.assessment) return undefined;

    for (const group of this.assessment.groups) {
      const task = group.tasks.find(t => t.id === taskId);
      if (task) return task;
    }

    return undefined;
  }

  /**
   * Schaltet die Anzeige einer Hilfe um
   */
  toggleGuide(id: string): void {
    this.showGuides[id] = !this.showGuides[id];
  }

  /**
   * Gibt zurück, ob eine Hilfe angezeigt werden soll
   */
  isGuideVisible(id: string): boolean {
    return this.showGuides[id] || false;
  }

  /**
   * Berechnet die Ergebnisse und speichert sie
   */
  calculateAndSave(): void {
    if (!this.assessment || !this.result) return;

    // Formularwerte in das Result-Objekt übertragen
    const formValue = this.form.value;
    let anyValueEntered = false;

    this.result.groupResults.forEach((groupResult, groupIndex) => {
      const groupFormValue = formValue.groups[groupIndex];

      groupResult.taskResults.forEach(taskResult => {
        const taskDef = this.getTaskDefinition(taskResult.taskId);
        if (taskDef) {
          if (this.assessment?.sideType === SideType.Both) {
            // Beidseitige Assessments - nur leftValue und rightValue speichern
            const leftValue = groupFormValue[`${taskResult.taskId}_left`];
            const rightValue = groupFormValue[`${taskResult.taskId}_right`];

            // Prüfen, ob Werte eingegeben wurden
            if (leftValue !== null && leftValue !== undefined) anyValueEntered = true;
            if (rightValue !== null && rightValue !== undefined) anyValueEntered = true;

            // Nur die seitenspezifischen Werte speichern
            taskResult.leftValue = leftValue;
            taskResult.rightValue = rightValue;
            
            // Keine Berechnung von "value" und keine Setzung von "side" mehr nötig
          } else {
            // Einseitige Assessments
            taskResult.value = groupFormValue[taskResult.taskId];
            if (taskResult.value !== null && taskResult.value !== undefined) anyValueEntered = true;
          }

          // Zusätzliche Daten für spezielle Konfigurationen
          if (taskDef.specialConfig) {
            taskResult.additionalData = taskResult.additionalData || {};
            Object.keys(taskDef.specialConfig).forEach(key => {
              const configValue = groupFormValue[`${taskResult.taskId}_${key}`];
              if (configValue !== null && configValue !== undefined) {
                taskResult.additionalData![key] = configValue;
                anyValueEntered = true;
              }
            });
          }
        }
      });
    });

    // Nur berechnen, wenn mindestens ein Wert eingegeben wurde
    if (!anyValueEntered) {
      this.error = 'Bitte geben Sie mindestens einen Wert ein, um die Berechnung durchzuführen.';
      return;
    }

    // Ergebnisse berechnen
    this.result = this.calculationService.calculateAssessmentResults(this.assessment, this.result);
    this.result.isComplete = true;
    this.error = ''; // Fehler zurücksetzen

    // Ergebnisse speichern
    this.resultService.saveResult(this.result).subscribe(
      savedResult => {
        this.result = savedResult;
        this.showResults = true;
        this.completed.emit(savedResult);
      },
      error => {
        console.error('Fehler beim Speichern der Ergebnisse:', error);
        this.error = 'Fehler beim Speichern der Ergebnisse.';
      }
    );
  }

  /**
   * Abbrechen und zurück
   */
  cancel(): void {
    this.canceled.emit();
  }

  /**
   * Gibt das Ergebnis einer Aufgabe zurück
   */
  getTaskResult(taskId: string): TaskResult | undefined {
    if (!this.result) return undefined;

    for (const groupResult of this.result.groupResults) {
      const taskResult = groupResult.taskResults.find(t => t.taskId === taskId);
      if (taskResult) return taskResult;
    }

    return undefined;
  }

  /**
   * Gibt das Ergebnis einer Gruppe zurück
   */
  getGroupResult(groupId: string): GroupResult | undefined {
    if (!this.result) return undefined;
    return this.result.groupResults.find(g => g.groupId === groupId);
  }

  /**
   * Gibt die Einträge der speziellen Konfiguration zurück
   */
  getSpecialConfigEntries(specialConfig: any): any[] {
    if (!specialConfig) return [];

    return Object.keys(specialConfig).map(key => ({
      key,
      label: specialConfig[key].label,
      inputType: specialConfig[key].inputType,
      options: specialConfig[key].options,
      minValue: specialConfig[key].minValue,
      maxValue: specialConfig[key].maxValue,
      step: specialConfig[key].step,
    }));
  }

  /**
   * Setzt alle Eingabefelder auf den maximal möglichen Wert
   * (Hilfsfunktion zum Testen)
   */
  setAllToMax(): void {
    if (!this.assessment || !this.form) return;

    const formValue = this.form.value;

    this.assessment.groups.forEach((group, groupIndex) => {
      const groupFormValue: any = {};

      group.tasks.forEach(task => {
        if (this.assessment?.sideType === SideType.Both) {
          // Bei beidseitigen Assessments beide Seiten auf Maximum setzen
          groupFormValue[`${task.id}_left`] = task.maxValue;
          groupFormValue[`${task.id}_right`] = task.maxValue;
        } else {
          // Bei einseitigen Assessments den Wert auf Maximum setzen
          groupFormValue[task.id] = task.maxValue;
        }

        // Spezielle Konfigurationen berücksichtigen (z.B. NHPT)
        if (task.specialConfig) {
          Object.keys(task.specialConfig).forEach(key => {
            const config = task.specialConfig[key];
            if (config.maxValue !== undefined) {
              groupFormValue[`${task.id}_${key}`] = config.maxValue;
            }
          });
        }
      });

      // Formularwerte aktualisieren
      this.form.get('groups')?.get([groupIndex])?.patchValue(groupFormValue);
    });
  }

  /**
   * Blendet alle Anleitungen ein
   * (Hilfsfunktion zum Testen)
   */
  showAllGuides(): void {
    if (!this.assessment) return;

    // Anleitung für das Assessment einblenden
    this.showGuides['assessment'] = true;

    // Anleitungen für alle Gruppen einblenden
    this.assessment.groups.forEach(group => {
      this.showGuides[group.id] = true;

      // Anleitungen für alle Tasks einblenden
      group.tasks.forEach(task => {
        this.showGuides[task.id] = true;
      });
    });
  }

  /**
   * Blendet alle Anleitungen ein oder aus
   * (Hilfsfunktion zum Testen)
   */
  toggleAllGuides(): void {
    if (!this.assessment) return;

    // Aktuellen Zustand umkehren
    this.allGuidesVisible = !this.allGuidesVisible;

    if (this.allGuidesVisible) {
      // Alle Anleitungen einblenden
      // Anleitung für das Assessment einblenden
      this.showGuides['assessment'] = true;

      // Anleitungen für alle Gruppen einblenden
      this.assessment.groups.forEach(group => {
        this.showGuides[group.id] = true;

        // Anleitungen für alle Tasks einblenden
        group.tasks.forEach(task => {
          this.showGuides[task.id] = true;
        });
      });
    } else {
      // Alle Anleitungen ausblenden
      // Anleitung für das Assessment ausblenden
      this.showGuides['assessment'] = false;

      // Anleitungen für alle Gruppen ausblenden
      this.assessment.groups.forEach(group => {
        this.showGuides[group.id] = false;

        // Anleitungen für alle Tasks ausblenden
        group.tasks.forEach(task => {
          this.showGuides[task.id] = false;
        });
      });
    }
  }
}
